import {
  type CollisionDetection,
  DndContext as DragAndDropContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { type Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import {
  createColumnOrderSettingsCollisionDetection,
  createRestrictToColumnsListAreaModifier,
  DATA_TABLE_COLUMNS_LIST_ITEM_BOTTOM_BORDER,
  DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT,
  type DraggingColumnInfo,
  getAllColumns,
  getColumnById,
  getDragHandleForColumn,
  getDragIndexes,
  getLeafColumnsForColumn,
  hideChildColumns,
  hideChildColumnsOnScroll,
  resetAllColumnsStyles,
  styleNonTargetColumns,
  transformColumnsOnDragOver,
} from './column-order-settings-dnd-utils.js';
import * as styles from './ColumnOrderSettingsDragAndDrop.sty.js';
import { devLog } from '../../../../../../core/utils/logging.js';
import type { Column } from '../../../../hooks/useTable/types.js';
import type { ColumnPinningState } from '../../../../tanstack-table-core-override/src/index.js';

// Threshold for detecting sudden jumps caused by virtualizer scroll corrections
// vs. gradual user/auto-scroll during drag.
const SCROLL_JUMP_THRESHOLD = DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT * 10;

/**
 * Drag and drop context for ordering in the column settings modal.
 * @internal
 */
export function ColumnOrderSettingsDragAndDropContext({
  children,
  localColumnOrder,
  updateLocalColumnOrder,
  localColumnPinning,
  itemVirtualizer,
}: Readonly<{
  children: ReactNode;
  localColumnOrder: string[];
  updateLocalColumnOrder: (newColumnOrder: string[]) => void;
  localColumnPinning: ColumnPinningState;
  itemVirtualizer: Virtualizer<HTMLDivElement, Element>;
}>) {
  const [dragOverlayContent, setDragOverlayContent] =
    useState<HTMLElement | null>(null);
  const [dragOverlaySize, setDragOverlaySize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [draggingColumnInfo, setDraggingColumnInfo] =
    useState<DraggingColumnInfo | null>(null);

  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const columnsListCollisionDetection = useMemo<CollisionDetection>(
    () =>
      createColumnOrderSettingsCollisionDetection(
        localColumnOrder,
        localColumnPinning,
        draggingColumnInfo,
      ),
    [draggingColumnInfo, localColumnOrder, localColumnPinning],
  );

  const restrictToColumnsListAreaModifier = useCallback<Modifier>(
    (args) => {
      return createRestrictToColumnsListAreaModifier(
        itemVirtualizer.scrollElement as HTMLDivElement | null,
      )(args);
    },
    [itemVirtualizer],
  );

  const resetDragging = (): void => {
    // Remove scroll event listener
    const scrollElement = itemVirtualizer.scrollElement;
    if (scrollElement && scrollHandlerRef.current) {
      scrollElement.removeEventListener('scroll', scrollHandlerRef.current);
      scrollHandlerRef.current = null;
    }

    // Reset all transforms
    if (modalContentRef.current) {
      const allColumns = getAllColumns(modalContentRef.current);
      resetAllColumnsStyles(allColumns);
    }

    modalContentRef.current = null;
    setDraggingColumnInfo(null);
    setDragOverlayContent(null);
    setDragOverlaySize({ width: 0, height: 0 });
  };

  const resetDraggingAndFocusLastActive = (activeId: string): void => {
    requestAnimationFrame(() => {
      // wait for the next frame to ensure the columns list finished updating
      getDragHandleForColumn(activeId, modalContentRef.current)?.focus();
      resetDragging();
    });
  };

  const handleOnDragStart = ({ active }: DragStartEvent): void => {
    const activeLocalColumn = active.data.current?.column as Column<
      unknown,
      unknown
    >;

    if (!activeLocalColumn) {
      return;
    }

    const scrollElement = itemVirtualizer.scrollElement as HTMLDivElement;
    modalContentRef.current =
      (scrollElement?.firstElementChild as HTMLDivElement) || scrollElement;

    const localColumn = getColumnById(
      modalContentRef.current,
      activeLocalColumn.id,
    );

    if (!localColumn) {
      devLog(
        '[DataTable] Could not find column element in DOM',
        activeLocalColumn.id,
      );
      return;
    }

    // Keep track of the dragged column
    // This is necessary because the active column may get virtualized out of the DOM during drag
    const columnInfo: DraggingColumnInfo = {
      id: active.id as string,
      index: active.data.current?.columnIndex,
      column: activeLocalColumn,
    };
    setDraggingColumnInfo(columnInfo);

    let lastScrollOffset =
      itemVirtualizer.scrollOffset ?? scrollElement?.scrollTop ?? 0;

    // Attach scroll event listener to handle virtualization during drag
    const handleScroll = (): void => {
      const innerContainer = modalContentRef.current;
      if (!innerContainer || !columnInfo.column) {
        return;
      }

      // Reapply disabled styles to non-target columns (including newly rendered ones)
      styleNonTargetColumns(innerContainer, columnInfo.column.parent?.id);
      // Reapply hidden styles to child columns of the dragged parent column
      hideChildColumnsOnScroll(innerContainer);

      const currentOffset = scrollElement.scrollTop;
      const scrollDelta = Math.abs(currentOffset - lastScrollOffset);
      if (scrollDelta >= SCROLL_JUMP_THRESHOLD) {
        // Large sudden jump - virtualizer correction, prevent it
        scrollElement.scrollTop = lastScrollOffset;
        return;
      }
      lastScrollOffset = currentOffset;
    };
    scrollHandlerRef.current = handleScroll;
    scrollElement?.addEventListener('scroll', handleScroll);

    // Apply styling to non-target columns once at drag start
    styleNonTargetColumns(
      modalContentRef.current,
      activeLocalColumn.parent?.id,
    );

    const dragOverlaySize = {
      width: localColumn.clientWidth,
      height: localColumn.clientHeight,
    };

    // Clone local column for drag overlay
    const clonedElement = localColumn.cloneNode(true) as HTMLElement;
    if (activeLocalColumn.columns.length === 0) {
      setDragOverlayContent(clonedElement);
    } else {
      // for nested columns, create a container and append the cloned parent and child columns
      // and use that container as the drag overlay content
      const wrapper = document.createElement('div');
      wrapper.appendChild(clonedElement);

      const childColumnIds = getLeafColumnsForColumn(
        activeLocalColumn,
        localColumnOrder,
      );

      // Clone each child column FIRST (before hiding them)
      for (const childColumnId of childColumnIds) {
        const childColumn = getColumnById(
          modalContentRef.current,
          childColumnId,
        );

        if (childColumn) {
          const clonedChild = childColumn.cloneNode(true) as HTMLElement;
          wrapper.appendChild(clonedChild);

          dragOverlaySize.height +=
            childColumn.clientHeight +
            DATA_TABLE_COLUMNS_LIST_ITEM_BOTTOM_BORDER;
        }
      }

      // Now hide child columns in the list and track them for virtualization
      // This must happen AFTER cloning so the clones don't have the isDragging class
      hideChildColumns(modalContentRef.current, childColumnIds);

      setDragOverlayContent(wrapper);
    }
    setDragOverlaySize(dragOverlaySize);
  };

  const handleOnDragOver = (event: DragOverEvent): void => {
    transformColumnsOnDragOver(
      modalContentRef.current,
      event,
      localColumnOrder,
      dragOverlaySize.height,
      draggingColumnInfo?.column,
    );
  };

  const handleOnDragEnd = ({ over }: DragEndEvent): void => {
    if (!draggingColumnInfo) {
      devLog('[Table] On drag end: dragging column info is undefined');
      resetDragging();
      return;
    }

    const activeColumn = draggingColumnInfo.column;
    const activeId = activeColumn.id;
    if (over === undefined || over === null) {
      devLog('[Table] On drag end: over info is undefined or null', over);
      resetDraggingAndFocusLastActive(activeId);
      return;
    }

    const overId = over.data.current?.columnId as string;
    // column order did not change
    if (overId === activeId) {
      resetDraggingAndFocusLastActive(activeId);
      return;
    }

    // Update the column order if dropped on a valid target
    const { fromIndex, toIndex } = getDragIndexes(
      localColumnOrder,
      activeId,
      overId,
    );

    const columnsToMove: string[] = [activeId];
    if (activeColumn.columns.length > 0) {
      // move all child columns if a parent column is moved
      const childColumnIds = getLeafColumnsForColumn(
        activeColumn,
        localColumnOrder,
      );
      columnsToMove.push(...childColumnIds);
    }

    const directionOffset = fromIndex < toIndex ? 1 : -1;
    const adjustedTargetIndex =
      fromIndex < toIndex
        ? Math.max(0, toIndex - columnsToMove.length + directionOffset)
        : toIndex;

    const newColumnOrder = [...localColumnOrder];
    newColumnOrder.splice(fromIndex, columnsToMove.length);
    newColumnOrder.splice(adjustedTargetIndex, 0, ...columnsToMove);
    updateLocalColumnOrder(newColumnOrder);
    resetDraggingAndFocusLastActive(activeId);
  };

  const onDragCancel = (): void => {
    const activeId = draggingColumnInfo?.id;
    if (activeId) {
      resetDraggingAndFocusLastActive(activeId);
    }
  };

  return (
    <DragAndDropContext
      // when the column order is updated, the DOM elements are re-rendered and the focus is lost
      // therefore, we need to manually restore the focus to the last active drag handle
      accessibility={{ restoreFocus: false }}
      sensors={sensors}
      collisionDetection={columnsListCollisionDetection}
      modifiers={[restrictToColumnsListAreaModifier]}
      onDragStart={handleOnDragStart}
      onDragOver={handleOnDragOver}
      onDragEnd={handleOnDragEnd}
      onDragCancel={onDragCancel}
    >
      {children}
      <DragOverlay
        dropAnimation={null}
        className={clsx(styles.columnDndOverlay)}
        style={{
          height: dragOverlaySize.height,
          width: dragOverlaySize.width,
        }}
      >
        <div
          ref={(node) =>
            dragOverlayContent && node?.appendChild(dragOverlayContent)
          }
        />
      </DragOverlay>
    </DragAndDropContext>
  );
}
